import { useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { format } from "date-fns"
import {
	IconAtSignOutline18 as AtSign,
	IconChevronDownOutline18 as ChevronDown,
	IconEnvelopeOutline18 as Mail,
	IconPlusOutline18 as Plus,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"
import { z } from "zod"

import { normalizeEmail } from "@productlane/api/src/lib/contact"
import { domainValidationRegex } from "@productlane/lib/constants"
import { generateId } from "@productlane/lib/src/browser"
import {
	Button,
	Dialog,
	Empty,
	Form,
	FormInput,
	IconButton,
	Loader,
	SubmitButton,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	useZodForm,
} from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import {
	EMAIL_MAXIMUM_LENGTH,
	EMAIL_MAXIMUM_LENGTH_ERROR_MESSAGE,
} from "@/constants"
import { isQuerySettled } from "@/lib/query-settled"
import { useZero } from "@/lib/zero"
import { SettingsHeader } from "../../settings-header"
import { SettingsContainer } from "../../settings-switches/settings-container"

const CreateBlockedEmailFormSchema = z
	.object({
		value: z.string().min(1, "Value is required"),
		type: z.enum(["EMAIL", "DOMAIN"]),
	})
	.superRefine((data, ctx) => {
		if (data.type === "EMAIL") {
			if (!z.string().email().safeParse(data.value).success) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Please enter a valid email address",
					path: ["value"],
				})
			}
			if (data.value.length > EMAIL_MAXIMUM_LENGTH) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: EMAIL_MAXIMUM_LENGTH_ERROR_MESSAGE,
					path: ["value"],
				})
			}
		} else if (data.type === "DOMAIN") {
			if (!domainValidationRegex.test(data.value)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: "Please enter a valid domain (e.g., example.com)",
					path: ["value"],
				})
			}
		}
	})

export const BlocklistSettingsPage = () => {
	const zero = useZero()
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
	const [deletingId, setDeletingId] = useState<string | null>(null)

	const [blockedEmails, blockedEmailsResult] = useQuery(
		queries.blockedSenders(),
	)

	const form = useZodForm({
		schema: CreateBlockedEmailFormSchema,
		defaultValues: {
			value: "",
			type: "EMAIL" as const,
		},
	})

	const handleCreate = async (
		values: z.infer<typeof CreateBlockedEmailFormSchema>,
	) => {
		const normalizedValue = normalizeEmail(values.value)

		const result = await zero.mutate(
			mutators.blockedSenders.create({
				id: generateId(),
				value: normalizedValue,
				type: values.type,
			}),
		).client

		if (result.type === "success") {
			toast.success({ title: "Blocked email sender added" })
			setIsCreateDialogOpen(false)
			form.reset()
		} else {
			toast.error({
				title: "Failed to add blocked email sender",
				description: result.error?.message ?? "Unknown error",
			})
		}
	}

	const handleDelete = async (id: string) => {
		setDeletingId(id)
		const result = await zero.mutate(mutators.blockedSenders.delete({ id }))
			.client

		if (result.type === "success") {
			toast.success({ title: "Blocked email sender removed" })
		} else {
			toast.error({
				title: "Failed to remove blocked email sender",
				description: result.error?.message ?? "Unknown error",
			})
		}
		setDeletingId(null)
	}

	return (
		<>
			<MetaInfo title="Blocked senders" />

			<SettingsHeader
				title="Blocklist"
				subtitle="Any feedback from blocked senders will be automatically ignored to help prevent spam and unwanted contact. Blocked messages are not stored and cannot be recovered."
			/>

			<div className="mb-4 flex items-center justify-between">
				<div className="font-semibold text-primary">Blocked Senders</div>
				<Button
					onClick={() => {
						setIsCreateDialogOpen(true)
					}}
					variant="outline"
					Icon={Plus}
				>
					Add
				</Button>
			</div>

			<SettingsContainer>
				{!isQuerySettled(blockedEmailsResult) ? (
					<div className="flex items-center justify-center py-8">
						<Loader />
					</div>
				) : blockedEmails.length === 0 ? (
					<Empty text="No blocked senders" className="py-8" />
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Value</TableHead>
								<TableHead>Type</TableHead>
								<TableHead>Blocked at</TableHead>
								<TableHead className="w-[4rem] text-right"></TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{blockedEmails.map((blocked) => (
								<TableRow key={blocked.id}>
									<TableCell className="max-w-80 truncate font-medium">
										{blocked.value}
									</TableCell>
									<TableCell>
										<span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-2.5 py-0.5 text-xxs font-medium text-gray-800 dark:bg-gray-700 dark:text-gray-200">
											{blocked.type === "DOMAIN" && (
												<AtSign className="h-3 w-3 text-green-500" />
											)}
											{blocked.type === "EMAIL" && (
												<Mail className="h-3 w-3 text-green-500" />
											)}
											{blocked.type}
										</span>
									</TableCell>
									<TableCell>
										<span className="inline-flex">
											{format(new Date(blocked.createdAt), "dd.MM.yyyy")}
										</span>
									</TableCell>
									<TableCell className="w-[4rem] text-right">
										<IconButton
											Icon={Trash}
											onClick={() => {
												void handleDelete(blocked.id)
											}}
											disabled={deletingId === blocked.id}
										/>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</SettingsContainer>

			<Dialog
				isOpen={isCreateDialogOpen}
				onOpenChange={(open) => {
					setIsCreateDialogOpen(open)
					if (!open) {
						setTimeout(() => {
							form.reset()
						}, 200)
					}
				}}
				title="Block sender"
				description="Block feedbacks from a specific address or domain"
			>
				<div className="mb-2 mt-4 text-sm">
					{form.watch("type") === "EMAIL" ? "Email address" : "Domain"}
				</div>
				<Form form={form} onSubmit={handleCreate} className="space-y-4">
					<div className="flex items-start justify-between gap-2">
						<DropdownMenu
							contentProps={{
								align: "start",
							}}
							trigger={
								<Button variant="outline" type="button" iconRight={ChevronDown}>
									<span>
										{form.watch("type") === "EMAIL" ? "Email" : "Domain"}
									</span>
								</Button>
							}
						>
							<DropdownMenuGroup>
								<DropdownMenuRadioGroup
									value={form.watch("type")}
									onValueChange={(value) => {
										form.setValue("type", value as "EMAIL" | "DOMAIN")
										form.setValue("value", "")
									}}
								>
									<DropdownMenuRadioItem value="EMAIL">
										Email
									</DropdownMenuRadioItem>
									<DropdownMenuRadioItem value="DOMAIN">
										Domain
									</DropdownMenuRadioItem>
								</DropdownMenuRadioGroup>
							</DropdownMenuGroup>
						</DropdownMenu>

						<FormInput
							name="value"
							placeholder={
								form.watch("type") === "EMAIL"
									? "example@email.com"
									: "example.com"
							}
						/>
					</div>
					<div className="flex justify-end">
						<SubmitButton className="w-28">Add</SubmitButton>
					</div>
				</Form>
			</Dialog>
		</>
	)
}
